import { Widget } from "@foretagsplatsen/widgetjs";
import ListSource from "../../list/ListSource.js";
import makeCardListWidget from "../list/CardListWidget/makeCardListWidget.js";
import ValidationResultWidget from "../ValidationResultWidget.js";

/**
 * Base widget to render a list of widgets where we can add/remove widgets.
 * It delegates the widget rendering to its strategy.
 *
 * @param {AbstractWidgetsListStrategy} strategy - Strategy used to render widget content
 */
export default class WidgetsListWidget extends Widget {
	constructor({ strategy }) {
		super(...arguments);

		this._strategy = strategy;

		this._strategy.onChange(() => {
			this._alterItems(() => {});
		});

		this._cardListWidget = this._makeCardListWidget();
		this._validationResult = new ValidationResultWidget();
		this._validateOnChange();
	}

	/**
	 * Validate the content of the widget.
	 *
	 *
	 * If the strategy tells us to externalize the validation rendering,
	 * we validate the strategy, and return the AlertCollector.
	 *
	 * If the strategy tells us to render it internally, then we validate the
	 * strategy and renders the error message if any.
	 * We then return true if the strategy is valid, false otherwise
	 *
	 * @param {object} [context] - Unspecified literal object provided
	 *   to the validation method
	 * @return {AlertCollector|boolean}
	 */
	validate(context) {
		const alertCollector = this._strategy.validation(context);
		if (this._strategy.shouldExternalizeValidationRendering()) {
			return alertCollector;
		}

		this._validationResult.setCollector(alertCollector);

		return !alertCollector.hasError();
	}

	/**
	 * Return true when the strategy is valid. False otherwise.
	 *
	 * @param {object} [context] - Unspecified literal object provided
	 *   to the validation method
	 * @return {boolean}
	 */
	isValid(context) {
		const alertCollector = this._strategy.validation(context);
		return alertCollector.isEmpty();
	}

	renderContentOn(html) {
		html.div(
			{
				class: [
					"WidgetsListWidget",
					{
						inline: this._strategy.isInlined(),
					},
					this._strategy.extraCSSClasses(),
				],
			},
			this._cardListWidget,
			this._validationResult,
		);
	}

	_renderTrailingWidgetOn(html) {
		if (!this._strategy.canAddItem()) {
			return;
		}

		html.div(
			{ class: "add-container", "data-test": "add-container" },
			this._strategy.addButtonRenderer({
				onClick: this._onAddClick.bind(this),
			}),
		);
	}

	_itemRenderer(item, index) {
		const content = this._strategy.itemRenderer({
			item,
			index,
		});

		return (html) =>
			html.div(
				{ class: "entry", "data-test": "entry" },
				html.div(
					{
						class: "content-container",
						"data-test": "content-container",
					},
					content,
				),
				this._deleteButtonRenderer({ item, index }),
			);
	}

	_deleteButtonRenderer({ item, index }) {
		const canBeDeleted = this._strategy.canDeleteItem({
			item,
			index,
		});

		if (!canBeDeleted) {
			return () => {};
		}

		return (html) =>
			html.div(
				{ class: "delete-container", "data-test": "delete-container" },
				this._strategy.deleteButtonRenderer({
					item,
					onClick: this._onDeleteClick.bind(this, { item, index }),
				}),
			);
	}

	_onAddClick() {
		this._alterItems(() => this._strategy.addNewItem());
	}

	_onDeleteClick({ item, index }) {
		this._alterItems(() => this._strategy.deleteItem({ item, index }));
	}

	_alterItems(fn) {
		fn();
		this._cardListWidget = this._makeCardListWidget();

		this._validateOnChange();
		this.update();
	}

	_validateOnChange() {
		if (!this._strategy.isValidatingOnChange()) {
			return;
		}

		this.validate();
	}

	_makeCardListWidget() {
		return makeCardListWidget({
			source: ListSource.makeAndFetch({
				stream: this._strategy.getListSourceStream(),
			}),
			itemRenderer: this._itemRenderer.bind(this),
			trailingWidget: this._renderTrailingWidgetOn.bind(this),
		});
	}
}
